import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowsLeftRight,
  Plus,
  MagnifyingGlass,
  ArrowUp,
  ArrowDown,
  Repeat,
  Wrench,
  Funnel,
  Calendar,
  Trash,
  Eye
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { CurrencyInput } from '../../components/CurrencyInput';
import { PageHeader } from './components/PageHeader';
import { CATEGORIAS, API, getCategoriaLabel } from './constants';

const TIPOS_MOVIMENTACAO = [
  { value: 'entrada', label: 'Entrada', icon: ArrowDown, color: '#22C55E', description: 'Adiciona ao estoque' },
  { value: 'saida', label: 'Saída', icon: ArrowUp, color: '#EF4444', description: 'Remove do estoque' },
  { value: 'consumo', label: 'Consumo', icon: Repeat, color: '#F59E0B', description: 'Uso interno/serviço' },
  { value: 'ajuste', label: 'Ajuste', icon: Wrench, color: '#6366F1', description: 'Correção de inventário' }
];

const emptyMovimentacao = {
  data: new Date().toISOString().split('T')[0],
  tipo: '',
  produto_id: '',
  quantidade: 1,
  unidade: '',
  quantidade_base: 0,
  valor_unitario: 0,
  valor_total: 0,
  observacao: '',
  responsavel: ''
};

export const Movimentacoes = () => {
  const navigate = useNavigate();
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyMovimentacao);
  const [saving, setSaving] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [movRes, prodRes] = await Promise.all([
        axios.get(`${API}/pacman/movimentacoes`),
        axios.get(`${API}/pacman/produtos`)
      ]);
      setMovimentacoes(movRes.data);
      setProdutos(prodRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tipo) {
      toast.error('Selecione o tipo de movimentação');
      return;
    }
    if (!formData.produto_id) {
      toast.error('Selecione um produto');
      return;
    }
    
    setSaving(true);
    
    try {
      // Calculate valor_total for Entrada
      const valorTotal = formData.tipo === 'entrada' && formData.valor_unitario 
        ? formData.quantidade * formData.valor_unitario 
        : 0;
      
      // Auto-fill unidade from produto if not set
      const submitData = {
        ...formData,
        unidade: formData.unidade || selectedProduto?.unidade || 'un',
        quantidade_base: formData.quantidade_base || formData.quantidade,
        valor_total: valorTotal
      };
      
      await axios.post(`${API}/pacman/movimentacoes`, submitData);
      
      // Show success message with cost info for Entrada
      if (formData.tipo === 'entrada' && formData.valor_unitario > 0) {
        toast.success(`Entrada registrada! Último Custo do produto atualizado para R$ ${formData.valor_unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      } else {
        toast.success('Movimentação registrada com sucesso!');
      }
      
      setIsDialogOpen(false);
      setFormData(emptyMovimentacao);
      setSelectedProduto(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar movimentação');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta movimentação?')) return;
    
    try {
      await axios.delete(`${API}/pacman/movimentacoes/${id}`);
      toast.success('Movimentação excluída com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir movimentação');
    }
  };

  const handleNewMovimentacao = () => {
    setFormData({...emptyMovimentacao, data: new Date().toISOString().split('T')[0]});
    setSelectedProduto(null);
    setIsDialogOpen(true);
  };

  const handleProdutoChange = (produtoId) => {
    const produto = produtos.find(p => p.id === produtoId);
    setSelectedProduto(produto);
    setFormData({
      ...formData, 
      produto_id: produtoId,
      unidade: produto?.unidade || 'un'
    });
  };

  // Agrupa produtos por categoria para melhor organização no select
  const produtosPorCategoria = useMemo(() => {
    const grouped = {};
    produtos.forEach(p => {
      const cat = p.categoria || 'sem-categoria';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(p);
    });
    return grouped;
  }, [produtos]);

  const filteredMovimentacoes = movimentacoes.filter(m => {
    const produto = produtos.find(p => p.id === m.produto_id);
    const matchesSearch = 
      produto?.produto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produto?.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.observacao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.responsavel?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filterTipo === 'all' || m.tipo === filterTipo;
    const matchesCategoria = filterCategoria === 'all' || produto?.categoria === filterCategoria;
    return matchesSearch && matchesTipo && matchesCategoria;
  });

  const getProdutoInfo = (produtoId) => {
    return produtos.find(p => p.id === produtoId) || { produto: 'Produto não encontrado', sku: '-', categoria: '' };
  };

  const getTipoInfo = (tipo) => {
    return TIPOS_MOVIMENTACAO.find(t => t.value === tipo) || { label: tipo, color: '#71717A', icon: ArrowsLeftRight };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  // Stats by tipo
  const stats = useMemo(() => {
    return TIPOS_MOVIMENTACAO.map(tipo => ({
      ...tipo,
      count: movimentacoes.filter(m => m.tipo === tipo.value).length,
      total: movimentacoes.filter(m => m.tipo === tipo.value).reduce((acc, m) => acc + (m.quantidade || 0), 0)
    }));
  }, [movimentacoes]);

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="movimentacoes-page">
      <Toaster position="top-right" richColors />
      <div className="salonup-container">
        <PageHeader 
          backTo="/gestao/pacman"
          backLabel="Voltar para PAC MAN"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Gestão', path: '/gestao' },
            { label: 'PAC MAN', path: '/gestao/pacman' },
            { label: 'Movimentações' }
          ]}
        />

        {/* Title Section */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#2D6A4F12' }}>
                <ArrowsLeftRight size={28} weight="regular" style={{ color: '#2D6A4F' }} />
              </div>
              <div>
                <h1 className="salonup-heading-lg">Movimentações</h1>
                <p className="salonup-text-sm mt-1">Controle de entrada, saída, consumo e ajustes</p>
              </div>
            </div>
            
            <Button 
              onClick={handleNewMovimentacao}
              data-testid="btn-new-movimentacao"
              className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl px-5 py-2.5 h-auto"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Nova Movimentação
            </Button>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          {stats.map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.value} className="salonup-card p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                    <Icon size={20} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#18181B]">{stat.count}</p>
                    <p className="salonup-text-xs">{stat.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Filters */}
        <section className="mb-6 animate-fade-in-up opacity-0 delay-250" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input 
                  placeholder="Buscar por produto, SKU, observação ou responsável..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-[#E4E4E7] rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-tipo">
                    <Funnel size={18} className="mr-2 text-[#A1A1AA]" />
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {TIPOS_MOVIMENTACAO.map(tipo => (
                      <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-56">
                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-categoria">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Movimentacoes Table */}
        <section className="animate-fade-in-up opacity-0 delay-300" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <p className="salonup-text-body">Carregando movimentações...</p>
              </div>
            ) : filteredMovimentacoes.length === 0 ? (
              <div className="p-12 text-center">
                <ArrowsLeftRight size={48} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
                <p className="salonup-heading-md mb-2">Nenhuma movimentação encontrada</p>
                <p className="salonup-text-sm mb-6">
                  {searchTerm || filterTipo !== 'all' || filterCategoria !== 'all'
                    ? 'Tente ajustar os filtros de busca'
                    : 'Registre sua primeira movimentação de estoque'}
                </p>
                {!searchTerm && filterTipo === 'all' && filterCategoria === 'all' && (
                  <Button 
                    onClick={handleNewMovimentacao}
                    className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl"
                  >
                    <Plus size={20} weight="bold" className="mr-2" />
                    Nova Movimentação
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="movimentacoes-table">
                  <thead className="bg-[#F7F6F4] border-b border-[#E4E4E7]">
                    <tr>
                      <th className="text-left p-4 salonup-label">Data</th>
                      <th className="text-left p-4 salonup-label">Tipo</th>
                      <th className="text-left p-4 salonup-label">SKU</th>
                      <th className="text-left p-4 salonup-label">Produto</th>
                      <th className="text-left p-4 salonup-label hidden lg:table-cell">Categoria</th>
                      <th className="text-right p-4 salonup-label">Qtd</th>
                      <th className="text-right p-4 salonup-label hidden md:table-cell">Valor</th>
                      <th className="text-left p-4 salonup-label hidden xl:table-cell">Observação</th>
                      <th className="text-center p-4 salonup-label">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMovimentacoes.map((mov, index) => {
                      const produto = getProdutoInfo(mov.produto_id);
                      const tipoInfo = getTipoInfo(mov.tipo);
                      const TipoIcon = tipoInfo.icon || ArrowsLeftRight;
                      
                      return (
                        <tr 
                          key={mov.id} 
                          className="border-b border-[#E4E4E7] hover:bg-[#F7F6F4]/50 transition-colors"
                          data-testid={`movimentacao-row-${index}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Calendar size={16} className="text-[#A1A1AA]" />
                              <span className="text-sm text-[#71717A]">{formatDate(mov.data)}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-7 h-7 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${tipoInfo.color}15` }}
                              >
                                <TipoIcon size={16} style={{ color: tipoInfo.color }} />
                              </div>
                              <span className="text-sm font-medium" style={{ color: tipoInfo.color }}>
                                {tipoInfo.label}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="font-mono text-sm text-[#71717A] bg-[#F4F4F5] px-2 py-0.5 rounded">
                              {produto.sku || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <p className="font-medium text-[#18181B]">{produto.produto}</p>
                            {produto.marca && (
                              <p className="text-xs text-[#71717A]">{produto.marca}</p>
                            )}
                          </td>
                          <td className="p-4 hidden lg:table-cell">
                            <span className="text-sm text-[#71717A]">{getCategoriaLabel(produto.categoria)}</span>
                          </td>
                          <td className="p-4 text-right">
                            <span className={`font-semibold text-base ${
                              mov.tipo === 'entrada' ? 'text-green-600' : 
                              mov.tipo === 'saida' || mov.tipo === 'consumo' ? 'text-red-500' : 
                              'text-[#18181B]'
                            }`}>
                              {mov.tipo === 'entrada' ? '+' : mov.tipo === 'saida' || mov.tipo === 'consumo' ? '-' : ''}
                              {mov.quantidade}
                            </span>
                            {mov.unidade && (
                              <span className="text-xs text-[#71717A] ml-1">{mov.unidade}</span>
                            )}
                          </td>
                          <td className="p-4 text-right hidden md:table-cell">
                            {mov.tipo === 'entrada' && mov.valor_unitario ? (
                              <div>
                                <p className="font-medium text-green-600">
                                  R$ {(mov.valor_total || (mov.valor_unitario * mov.quantidade)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                                <p className="text-xs text-[#A1A1AA]">
                                  R$ {mov.valor_unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/un
                                </p>
                              </div>
                            ) : (
                              <span className="text-sm text-[#A1A1AA]">-</span>
                            )}
                          </td>
                          <td className="p-4 hidden xl:table-cell">
                            <span className="text-sm text-[#71717A] truncate max-w-[150px] block">
                              {mov.observacao || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-center">
                              <button 
                                onClick={() => handleDelete(mov.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`btn-delete-${index}`}
                                title="Excluir movimentação"
                              >
                                <Trash size={18} className="text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {filteredMovimentacoes.length > 0 && (
            <p className="salonup-text-xs mt-4 text-center">
              {filteredMovimentacoes.length} movimentação{filteredMovimentacoes.length !== 1 ? 'ões' : ''} encontrada{filteredMovimentacoes.length !== 1 ? 's' : ''}
            </p>
          )}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salão moderno</p>
        </footer>
      </div>

      {/* Movimentacao Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="salonup-heading-md">Nova Movimentação</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {/* Tipo de Movimentação - Cards visuais */}
            <div>
              <label className="salonup-label mb-3 block">Tipo de Movimentação *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TIPOS_MOVIMENTACAO.map(tipo => {
                  const Icon = tipo.icon;
                  const isSelected = formData.tipo === tipo.value;
                  return (
                    <button
                      key={tipo.value}
                      type="button"
                      onClick={() => setFormData({...formData, tipo: tipo.value})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        isSelected 
                          ? 'border-current bg-opacity-10' 
                          : 'border-[#E4E4E7] hover:border-[#A1A1AA]'
                      }`}
                      style={isSelected ? { borderColor: tipo.color, backgroundColor: `${tipo.color}10` } : {}}
                    >
                      <Icon size={24} className="mx-auto mb-1" style={{ color: isSelected ? tipo.color : '#71717A' }} />
                      <p className="text-sm font-medium" style={{ color: isSelected ? tipo.color : '#18181B' }}>
                        {tipo.label}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Data */}
            <div>
              <label className="salonup-label mb-2 block">Data *</label>
              <Input 
                type="date"
                value={formData.data}
                onChange={(e) => setFormData({...formData, data: e.target.value})}
                className="h-11 border-[#E4E4E7] rounded-xl"
                required
              />
            </div>

            {/* Produto */}
            <div>
              <label className="salonup-label mb-2 block">Produto *</label>
              <Select 
                value={formData.produto_id} 
                onValueChange={handleProdutoChange}
                required
              >
                <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl">
                  <SelectValue placeholder="Selecione o produto" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(produtosPorCategoria).map(([categoria, prods]) => (
                    <div key={categoria}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-[#71717A] bg-[#F7F6F4] sticky top-0">
                        {getCategoriaLabel(categoria)}
                      </div>
                      {prods.map(prod => (
                        <SelectItem key={prod.id} value={prod.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-[#71717A]">{prod.sku}</span>
                            <span>{prod.produto}</span>
                            {prod.marca && <span className="text-xs text-[#A1A1AA]">({prod.marca})</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {produtos.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Cadastre produtos primeiro para registrar movimentações.
                </p>
              )}
            </div>

            {/* Produto Info Preview */}
            {selectedProduto && (
              <div className="p-3 bg-[#F7F6F4] rounded-xl border border-[#E4E4E7]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-[#E4E4E7]">
                    <Eye size={18} className="text-[#71717A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#18181B] truncate">{selectedProduto.produto}</p>
                    <p className="text-xs text-[#71717A]">
                      {selectedProduto.sku} · {selectedProduto.marca || 'Sem marca'} · {getCategoriaLabel(selectedProduto.categoria)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#71717A]">Último Custo</p>
                    <p className="font-medium text-[#18181B]">
                      {selectedProduto.custo_unitario 
                        ? `R$ ${selectedProduto.custo_unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                        : 'Não informado'}
                    </p>
                  </div>
                  <div className="text-right border-l border-[#E4E4E7] pl-3 ml-2">
                    <p className="text-xs text-[#71717A]">Unidade</p>
                    <p className="font-medium text-[#18181B]">{selectedProduto.unidade || 'un'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quantidade e Unidade */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="salonup-label mb-2 block">Quantidade *</label>
                <Input 
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={formData.quantidade}
                  onChange={(e) => setFormData({...formData, quantidade: parseFloat(e.target.value) || 0})}
                  className="h-11 border-[#E4E4E7] rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="salonup-label mb-2 block">Unidade</label>
                <Input 
                  value={formData.unidade}
                  onChange={(e) => setFormData({...formData, unidade: e.target.value})}
                  placeholder={selectedProduto?.unidade || 'un'}
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>
            </div>

            {/* Valor Unitário e Valor Total - Apenas para Entrada */}
            {formData.tipo === 'entrada' && (
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="salonup-label text-green-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Informações de Custo (Entrada)
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="salonup-label mb-2 block">Valor Unitário</label>
                    <CurrencyInput 
                      value={formData.valor_unitario}
                      onChange={(val) => setFormData({...formData, valor_unitario: val})}
                      className="border-green-200 bg-white"
                      data-testid="input-valor-unitario"
                    />
                    <p className="text-xs text-green-600 mt-1">
                      Atualiza o Último Custo do produto
                    </p>
                  </div>
                  <div>
                    <label className="salonup-label mb-2 block">Valor Total</label>
                    <div className="h-11 border border-green-200 rounded-xl bg-green-100/50 flex items-center px-3">
                      <span className="font-semibold text-green-700">
                        {formData.valor_unitario && formData.quantidade 
                          ? `R$ ${(formData.valor_unitario * formData.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'R$ 0,00'
                        }
                      </span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      Calculado automaticamente
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Quantidade Base (opcional) */}
            <div>
              <label className="salonup-label mb-2 block">
                Quantidade Base 
                <span className="text-[#A1A1AA] font-normal ml-1">(opcional)</span>
              </label>
              <Input 
                type="number"
                min="0"
                step="0.01"
                value={formData.quantidade_base || ''}
                onChange={(e) => setFormData({...formData, quantidade_base: parseFloat(e.target.value) || 0})}
                placeholder="Ex: quantidade em ml, g, etc."
                className="h-11 border-[#E4E4E7] rounded-xl"
              />
              <p className="text-xs text-[#A1A1AA] mt-1">
                Use para registrar a quantidade em unidade base diferente (ex: total em ml)
              </p>
            </div>

            {/* Observação */}
            <div>
              <label className="salonup-label mb-2 block">Observação</label>
              <Input 
                value={formData.observacao}
                onChange={(e) => setFormData({...formData, observacao: e.target.value})}
                placeholder="Ex: Compra mensal, Uso em cliente, Ajuste de inventário..."
                className="h-11 border-[#E4E4E7] rounded-xl"
              />
            </div>

            {/* Responsável */}
            <div>
              <label className="salonup-label mb-2 block">Responsável</label>
              <Input 
                value={formData.responsavel}
                onChange={(e) => setFormData({...formData, responsavel: e.target.value})}
                placeholder="Nome do responsável pela movimentação"
                className="h-11 border-[#E4E4E7] rounded-xl"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E4E4E7]">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="rounded-xl px-5"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving || produtos.length === 0}
                className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl px-5"
              >
                {saving ? 'Salvando...' : 'Registrar Movimentação'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Movimentacoes;
